<template>
  <div class="dice-game">
    <!-- Game Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-dice" :size="18" /> {{ $t('games.dice.title') }}
        <span class="game-tag">{{ $t('games.dice.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button
          class="icon-btn"
          :class="{ active: soundEnabled }"
          :title="$t('games.toggle_sound')"
          @click="soundEnabled = !soundEnabled"
        >
          <AppIcon :name="soundEnabled ? 'sound-on' : 'sound-off'" :size="16" />
        </button>
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas Area -->
      <div class="game-canvas-area">
        <div class="canvas-bg-grid"></div>
        <div class="canvas-bg-glow"></div>

        <!-- Roll result display -->
        <div class="dice-result-display" :class="resultClass">
          <div class="dice-result-number" :class="resultClass">
            {{ displayRoll }}
          </div>
        </div>

        <!-- Slider + Direction -->
        <div class="dice-controls">
          <div>
            <label class="ctrl-label">{{ $t('games.dice.target_value') }}</label>
            <div class="dice-slider-wrap">
              <div
                class="dice-slider-track"
                :style="{ background: sliderBackground }"
                ref="sliderTrackRef"
                @mousedown="onSliderMouseDown"
                @touchstart.prevent="onSliderTouchStart"
              >
                <div
                  class="dice-slider-handle"
                  :style="{ left: sliderPercent + '%' }"
                ></div>
              </div>
              <input
                type="number"
                class="dice-slider-input"
                :value="target.toFixed(2)"
                min="0.01"
                max="99.98"
                step="0.01"
                @change="onTargetInputChange"
              />
            </div>
          </div>

          <div class="dice-dir-toggle">
            <button
              class="dice-dir-btn"
              :class="{ active: direction === 'under' }"
              @click="setDirection('under')"
            >
              {{ $t('games.dice.under') }}
            </button>
            <button
              class="dice-dir-btn"
              :class="{ active: direction === 'over' }"
              @click="setDirection('over')"
            >
              {{ $t('games.dice.over') }}
            </button>
          </div>
        </div>

        <!-- History Strip -->
        <div v-if="rollHistory.length > 0" class="history-strip">
          <div
            v-for="entry in rollHistory"
            :key="entry.id"
            class="history-pill"
            :class="entry.is_win ? 'history-win' : 'history-loss'"
            :title="`${entry.displayRoll} — ${entry.direction} ${entry.target.toFixed(2)} — ${entry.is_win ? 'WIN' : 'LOSS'}`"
          >
            {{ entry.displayRoll }}
          </div>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button
            class="game-tab"
            :class="{ active: activeTab === 'bet' }"
            @click="activeTab = 'bet'"
          >{{ $t('games.bet_tab') }}</button>
          <button
            class="game-tab"
            :class="{ active: activeTab === 'auto' }"
            @click="activeTab = 'auto'"
          >{{ $t('games.auto_tab') }}</button>
          <button class="game-tab" @click="router.push({ name: 'player.provably-fair' })">
            {{ $t('games.rules_tab') }}
          </button>
        </div>

        <!-- Bet Tab -->
        <div v-if="activeTab === 'bet'" class="game-tab-content">
          <label class="ctrl-label">{{ $t('games.bet_amount') }}</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              class="bet-input"
              type="number"
              v-model.number="betAmount"
              min="0.01"
              step="0.01"
              :disabled="rolling || isAutoRunning"
            />
          </div>
          <div class="bet-shortcuts">
            <button class="bet-short" :disabled="rolling || isAutoRunning" @click="halveBet">½</button>
            <button class="bet-short" :disabled="rolling || isAutoRunning" @click="doubleBet">2×</button>
            <button class="bet-short" :disabled="rolling || isAutoRunning" @click="maxBet">Max</button>
          </div>

          <div class="dice-stats-grid">
            <div class="dice-stat-item">
              <div class="dice-stat-label">{{ $t('games.dice.win_chance') }}</div>
              <div class="dice-stat-value">{{ winChance.toFixed(2) }}%</div>
            </div>
            <div class="dice-stat-item">
              <div class="dice-stat-label">{{ $t('games.multiplier') }}</div>
              <div class="dice-stat-value gold">{{ multiplier.toFixed(4) }}×</div>
            </div>
            <div class="dice-stat-item">
              <div class="dice-stat-label">{{ $t('games.dice.profit_on_win') }}</div>
              <div class="dice-stat-value success">+${{ profitOnWin.toFixed(2) }}</div>
            </div>
          </div>

          <button class="btn-action" :disabled="rolling || isAutoRunning" @click="rollDice">
            {{ rolling ? $t('games.dice.rolling') : $t('games.dice.roll_dice') }}
          </button>

          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>

        <!-- Auto-bet Tab -->
        <div v-else-if="activeTab === 'auto'" class="game-tab-content">
          <!-- Number of bets -->
          <label class="ctrl-label">{{ $t('games.number_of_bets') }}</label>
          <div class="bet-input-wrap" style="margin-bottom: 12px;">
            <input
              class="bet-input"
              type="number"
              v-model.number="autoBets"
              min="1"
              max="1000"
              step="1"
              :disabled="isAutoRunning"
              style="padding-left: 10px;"
            />
          </div>

          <!-- Stop on profit / loss -->
          <div class="auto-stop-row">
            <div class="auto-stop-item">
              <label class="ctrl-label">{{ $t('games.stop_on_profit') }}</label>
              <div class="bet-input-wrap">
                <span class="bet-currency">$</span>
                <input
                  class="bet-input"
                  type="number"
                  v-model.number="autoStopProfit"
                  min="0"
                  step="0.01"
                  :disabled="isAutoRunning"
                  placeholder="0 = off"
                />
              </div>
            </div>
            <div class="auto-stop-item">
              <label class="ctrl-label">{{ $t('games.stop_on_loss') }}</label>
              <div class="bet-input-wrap">
                <span class="bet-currency">$</span>
                <input
                  class="bet-input"
                  type="number"
                  v-model.number="autoStopLoss"
                  min="0"
                  step="0.01"
                  :disabled="isAutoRunning"
                  placeholder="0 = off"
                />
              </div>
            </div>
          </div>

          <!-- On Win -->
          <label class="ctrl-label" style="margin-top: 10px;">{{ $t('games.on_win') }}</label>
          <div class="auto-action-row">
            <div class="auto-action-toggles">
              <button
                class="auto-action-btn"
                :class="{ active: autoOnWinAction === 'reset' }"
                :disabled="isAutoRunning"
                @click="autoOnWinAction = 'reset'"
              >{{ $t('games.reset') }}</button>
              <button
                class="auto-action-btn"
                :class="{ active: autoOnWinAction === 'increase' }"
                :disabled="isAutoRunning"
                @click="autoOnWinAction = 'increase'"
              >{{ $t('games.increase_by') }}</button>
            </div>
            <div v-if="autoOnWinAction === 'increase'" class="auto-pct-wrap">
              <input
                class="bet-input"
                type="number"
                v-model.number="autoOnWinPercent"
                min="1"
                max="1000"
                step="1"
                :disabled="isAutoRunning"
                style="padding-left: 10px; width: 70px;"
              />
              <span class="pct-label">%</span>
            </div>
          </div>

          <!-- On Loss -->
          <label class="ctrl-label" style="margin-top: 10px;">{{ $t('games.on_loss') }}</label>
          <div class="auto-action-row">
            <div class="auto-action-toggles">
              <button
                class="auto-action-btn"
                :class="{ active: autoOnLossAction === 'reset' }"
                :disabled="isAutoRunning"
                @click="autoOnLossAction = 'reset'"
              >{{ $t('games.reset') }}</button>
              <button
                class="auto-action-btn"
                :class="{ active: autoOnLossAction === 'increase' }"
                :disabled="isAutoRunning"
                @click="autoOnLossAction = 'increase'"
              >{{ $t('games.increase_by') }}</button>
            </div>
            <div v-if="autoOnLossAction === 'increase'" class="auto-pct-wrap">
              <input
                class="bet-input"
                type="number"
                v-model.number="autoOnLossPercent"
                min="1"
                max="1000"
                step="1"
                :disabled="isAutoRunning"
                style="padding-left: 10px; width: 70px;"
              />
              <span class="pct-label">%</span>
            </div>
          </div>

          <!-- Progress bar (while running) -->
          <div v-if="isAutoRunning" class="auto-progress">
            <div class="auto-progress-bar-wrap">
              <div
                class="auto-progress-bar"
                :style="{ width: `${Math.min(100, (autoProgress / autoBets) * 100)}%` }"
              ></div>
            </div>
            <div class="auto-progress-info">
              <span>{{ autoProgress }}/{{ autoBets }} rolls</span>
              <span :class="autoNetProfit >= 0 ? 'profit-pos' : 'profit-neg'">
                {{ autoNetProfit >= 0 ? '+' : '' }}${{ autoNetProfit.toFixed(2) }}
              </span>
            </div>
          </div>

          <!-- Start / Stop button -->
          <button
            class="btn-action"
            :class="{ 'btn-stop': isAutoRunning }"
            :disabled="rolling && !isAutoRunning"
            @click="isAutoRunning ? stopAutoBet() : startAutoBet()"
            style="margin-top: 12px;"
          >
            {{ isAutoRunning ? $t('games.stop_auto') : $t('games.start_auto_bet') }}
          </button>

          <div class="balance-row">
            <span class="bal-label">{{ $t('games.balance') }}</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import AppIcon from '@/components/AppIcon.vue'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'

const ROLL_RANGE = 10000
const HOUSE_EDGE = 0.01 // 1%
const MIN_TARGET = 0.01
const MAX_TARGET = 99.98
const AUTO_SETTINGS_KEY = 'dice_auto_settings'

const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toastStore = useToastStore()

// --- Game State ---
const target = ref(50.0)
const direction = ref<'over' | 'under'>('under')
const betAmount = ref(1.0)
const rolling = ref(false)
const displayRoll = ref('50.00')
const resultClass = ref<'win' | 'loss' | ''>('')
const soundEnabled = ref(true)

// Slider DOM ref
const sliderTrackRef = ref<HTMLElement | null>(null)
let isDragging = false

// --- Tab ---
const activeTab = ref<'bet' | 'auto'>('bet')

// --- History ---
interface HistoryEntry {
  id: number
  displayRoll: string
  target: number
  direction: 'over' | 'under'
  is_win: boolean
  bet: number
  profit: number
}

const rollHistory = ref<HistoryEntry[]>([])
let historyIdCounter = 0

// --- Auto-bet State ---
const isAutoRunning = ref(false)
let autoBetAborted = false

const autoBets = ref(10)
const autoStopProfit = ref(0)
const autoStopLoss = ref(0)
const autoOnWinAction = ref<'reset' | 'increase'>('reset')
const autoOnWinPercent = ref(10)
const autoOnLossAction = ref<'reset' | 'increase'>('reset')
const autoOnLossPercent = ref(10)

const autoProgress = ref(0)
const autoNetProfit = ref(0)

// --- Computed ---
const targetInt = computed(() => Math.round(target.value * 100))

const winCount = computed(() =>
  direction.value === 'under'
    ? targetInt.value
    : ROLL_RANGE - 1 - targetInt.value,
)

const winChance = computed(() => Math.max(0, (winCount.value / ROLL_RANGE) * 100))

const multiplier = computed(() => {
  if (winCount.value <= 0) return 0
  return (ROLL_RANGE / winCount.value) * (1 - HOUSE_EDGE)
})

const profitOnWin = computed(() =>
  betAmount.value > 0 ? betAmount.value * (multiplier.value - 1) : 0,
)

// Slider position: target maps directly to 0-100%
const sliderPercent = computed(() =>
  Math.max(0, Math.min(100, target.value)),
)

const sliderBackground = computed(() => {
  const pos = target.value
  if (direction.value === 'under') {
    return `linear-gradient(90deg, rgba(0,231,1,.12) 0%, rgba(0,231,1,.12) ${pos}%, rgba(255,255,255,.04) ${pos}%, rgba(255,255,255,.04) 100%)`
  }
  return `linear-gradient(90deg, rgba(255,255,255,.04) 0%, rgba(255,255,255,.04) ${pos}%, rgba(0,231,1,.12) ${pos}%, rgba(0,231,1,.12) 100%)`
})

const formattedBalance = computed(() => {
  const bal = walletStore.balance?.balance
  if (bal == null) return '—'
  return `$${bal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
})

// --- Helpers ---
function clampTarget(val: number): number {
  return Math.max(MIN_TARGET, Math.min(MAX_TARGET, Math.round(val * 100) / 100))
}

function setDirection(dir: 'over' | 'under') {
  direction.value = dir
}

function halveBet() {
  betAmount.value = Math.max(0.01, +(betAmount.value / 2).toFixed(2))
}

function doubleBet() {
  betAmount.value = +(betAmount.value * 2).toFixed(2)
}

function maxBet() {
  const bal = walletStore.balance?.balance
  if (bal != null && bal > 0) {
    betAmount.value = +bal.toFixed(2)
  }
}

// --- Slider drag ---
function updateTargetFromX(clientX: number) {
  const track = sliderTrackRef.value
  if (!track) return
  const rect = track.getBoundingClientRect()
  const x = clientX - rect.left
  const pct = Math.max(0, Math.min(100, (x / rect.width) * 100))
  target.value = clampTarget(pct)
}

function onSliderMouseDown(e: MouseEvent) {
  isDragging = true
  updateTargetFromX(e.clientX)
  document.addEventListener('mousemove', onDocMouseMove)
  document.addEventListener('mouseup', onDocMouseUp)
}

function onDocMouseMove(e: MouseEvent) {
  if (isDragging) updateTargetFromX(e.clientX)
}

function onDocMouseUp() {
  isDragging = false
  document.removeEventListener('mousemove', onDocMouseMove)
  document.removeEventListener('mouseup', onDocMouseUp)
}

function onSliderTouchStart(e: TouchEvent) {
  isDragging = true
  if (e.touches[0]) updateTargetFromX(e.touches[0].clientX)
  document.addEventListener('touchmove', onDocTouchMove, { passive: false })
  document.addEventListener('touchend', onDocTouchEnd)
}

function onDocTouchMove(e: TouchEvent) {
  e.preventDefault()
  if (isDragging && e.touches[0]) updateTargetFromX(e.touches[0].clientX)
}

function onDocTouchEnd() {
  isDragging = false
  document.removeEventListener('touchmove', onDocTouchMove)
  document.removeEventListener('touchend', onDocTouchEnd)
}

function onTargetInputChange(e: Event) {
  const input = e.target as HTMLInputElement
  const val = parseFloat(input.value)
  if (!isNaN(val)) {
    target.value = clampTarget(val)
  }
  input.value = target.value.toFixed(2)
}

// --- Roll Animation ---
function animateRoll(finalValue: string): Promise<void> {
  return new Promise((resolve) => {
    const startTime = Date.now()
    const duration = 800

    const interval = setInterval(() => {
      const elapsed = Date.now() - startTime
      if (elapsed >= duration) {
        clearInterval(interval)
        displayRoll.value = finalValue
        resolve()
        return
      }
      displayRoll.value = (Math.random() * 100).toFixed(2)
    }, 30)
  })
}

// --- Core Roll Logic ---
interface PlayResponse {
  payout: number
  multiplier: number
  is_win: boolean
  outcome_data: { roll: number }
  game_round_id: string
  balance: { bonus_used: number; cash_used: number }
}

interface RollResult {
  roll: number
  displayRoll: string
  is_win: boolean
  payout: number
  bet: number
}

async function runRoll(bet: number): Promise<RollResult | null> {
  const currentTarget = target.value
  const currentDirection = direction.value

  resultClass.value = ''

  try {
    const response = await apiClient.post<PlayResponse>(`/api/games/dice/play`, {
      amount: bet.toFixed(2),
      options: {
        target: currentTarget,
        direction: currentDirection,
      },
    })

    const { outcome_data, is_win, payout } = response.data
    const finalValue = (outcome_data.roll / 100).toFixed(2)

    await animateRoll(finalValue)

    resultClass.value = is_win ? 'win' : 'loss'
    setTimeout(() => { resultClass.value = '' }, 1500)

    // Add to history (newest first, max 20)
    rollHistory.value.unshift({
      id: historyIdCounter++,
      displayRoll: finalValue,
      target: currentTarget,
      direction: currentDirection,
      is_win,
      bet,
      profit: payout - bet,
    })
    if (rollHistory.value.length > 20) {
      rollHistory.value = rollHistory.value.slice(0, 20)
    }

    return { roll: outcome_data.roll, displayRoll: finalValue, is_win, payout, bet }
  } catch (err: unknown) {
    const axiosError = err as { response?: { data?: { message?: string } } }
    const msg = axiosError?.response?.data?.message ?? 'Failed to roll. Please try again.'
    toastStore.error(msg)
    displayRoll.value = target.value.toFixed(2)
    return null
  }
}

// --- Manual Roll ---
async function rollDice() {
  if (rolling.value || isAutoRunning.value) return

  const bet = betAmount.value
  if (!bet || bet <= 0) {
    toastStore.error(t('games.invalid_bet_amount'))
    return
  }

  const bal = walletStore.balance?.balance ?? 0
  if (bet > bal) {
    toastStore.error(t('games.insufficient_balance'))
    return
  }

  rolling.value = true

  const result = await runRoll(bet)

  if (result) {
    if (result.is_win) {
      const profit = result.payout - bet
      toastStore.success(`WIN +$${profit.toFixed(2)}`)
    } else {
      toastStore.error(`LOSS -$${bet.toFixed(2)}`)
    }
    walletStore.applyGameResult(bet, result.payout)
  }

  rolling.value = false
}

// --- Auto-bet ---
async function startAutoBet() {
  if (isAutoRunning.value || rolling.value) return

  autoBetAborted = false
  isAutoRunning.value = true
  autoProgress.value = 0
  autoNetProfit.value = 0

  const baseBet = betAmount.value
  let currentBet = baseBet
  let betsLeft = autoBets.value

  while (betsLeft > 0 && !autoBetAborted) {
    const bal = walletStore.balance?.balance ?? 0
    if (currentBet > bal) {
      toastStore.error(t('games.autobet_stopped_balance'))
      break
    }

    betAmount.value = currentBet
    rolling.value = true
    const result = await runRoll(currentBet)
    rolling.value = false

    if (!result) break

    autoProgress.value++
    betsLeft--

    const roundProfit = result.payout - currentBet
    autoNetProfit.value += roundProfit

    // Refresh balance after each roll
    walletStore.applyGameResult(currentBet, result.payout)

    // Check stop conditions
    if (autoStopProfit.value > 0 && autoNetProfit.value >= autoStopProfit.value) {
      toastStore.success(t('games.autobet_stopped_profit', { amount: autoNetProfit.value.toFixed(2) }))
      break
    }
    if (autoStopLoss.value > 0 && autoNetProfit.value <= -autoStopLoss.value) {
      toastStore.error(t('games.autobet_stopped_loss', { amount: (-autoNetProfit.value).toFixed(2) }))
      break
    }

    // Adjust bet for next round based on win/loss strategy
    if (result.is_win) {
      currentBet = autoOnWinAction.value === 'reset'
        ? baseBet
        : +(currentBet * (1 + autoOnWinPercent.value / 100)).toFixed(2)
    } else {
      currentBet = autoOnLossAction.value === 'reset'
        ? baseBet
        : +(currentBet * (1 + autoOnLossPercent.value / 100)).toFixed(2)
    }

    // Clamp bet to sane min
    currentBet = Math.max(0.01, currentBet)
  }

  // Restore original base bet
  betAmount.value = baseBet
  isAutoRunning.value = false
  rolling.value = false

  if (!autoBetAborted && autoProgress.value > 0) {
    const netStr = autoNetProfit.value >= 0
      ? `+$${autoNetProfit.value.toFixed(2)}`
      : `-$${(-autoNetProfit.value).toFixed(2)}`
    toastStore.success(`Auto-bet complete: ${autoProgress.value} rolls, net ${netStr}`)
  }

  await walletStore.fetchBalance()
}

function stopAutoBet() {
  autoBetAborted = true
}

// --- Session persistence for auto settings ---
function saveAutoSettings() {
  try {
    sessionStorage.setItem(AUTO_SETTINGS_KEY, JSON.stringify({
      autoBets: autoBets.value,
      autoStopProfit: autoStopProfit.value,
      autoStopLoss: autoStopLoss.value,
      autoOnWinAction: autoOnWinAction.value,
      autoOnWinPercent: autoOnWinPercent.value,
      autoOnLossAction: autoOnLossAction.value,
      autoOnLossPercent: autoOnLossPercent.value,
    }))
  } catch {
    // sessionStorage may be unavailable in some contexts
  }
}

function loadAutoSettings() {
  try {
    const raw = sessionStorage.getItem(AUTO_SETTINGS_KEY)
    if (!raw) return
    const data = JSON.parse(raw) as Record<string, unknown>
    if (typeof data.autoBets === 'number') autoBets.value = data.autoBets
    if (typeof data.autoStopProfit === 'number') autoStopProfit.value = data.autoStopProfit
    if (typeof data.autoStopLoss === 'number') autoStopLoss.value = data.autoStopLoss
    if (data.autoOnWinAction === 'reset' || data.autoOnWinAction === 'increase') autoOnWinAction.value = data.autoOnWinAction
    if (typeof data.autoOnWinPercent === 'number') autoOnWinPercent.value = data.autoOnWinPercent
    if (data.autoOnLossAction === 'reset' || data.autoOnLossAction === 'increase') autoOnLossAction.value = data.autoOnLossAction
    if (typeof data.autoOnLossPercent === 'number') autoOnLossPercent.value = data.autoOnLossPercent
  } catch {
    // ignore
  }
}

watch(
  [autoBets, autoStopProfit, autoStopLoss, autoOnWinAction, autoOnWinPercent, autoOnLossAction, autoOnLossPercent],
  saveAutoSettings,
)

// Space bar shortcut to roll
function onKeyDown(e: KeyboardEvent) {
  if (e.code === 'Space' && !rolling.value && !isAutoRunning.value) {
    e.preventDefault()
    rollDice()
  }
}

onMounted(() => {
  document.addEventListener('keydown', onKeyDown)
  if (!walletStore.balance) {
    walletStore.fetchBalance()
  }
  loadAutoSettings()
})

onUnmounted(() => {
  autoBetAborted = true
  document.removeEventListener('keydown', onKeyDown)
  document.removeEventListener('mousemove', onDocMouseMove)
  document.removeEventListener('mouseup', onDocMouseUp)
  document.removeEventListener('touchmove', onDocTouchMove)
  document.removeEventListener('touchend', onDocTouchEnd)
})
</script>

<style scoped>
/* Layout */
.dice-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  animation: fadeIn 0.25s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}

.btn-back:hover {
  color: var(--color-on-dark, #fff);
  background: var(--bg-hover, #2a4455);
}

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  border: none;
}

.icon-btn:hover,
.icon-btn.active {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
}

/* Game body: canvas + sidebar */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  flex-direction: column;
  gap: 24px;
  padding: 32px;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.025) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.025) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.canvas-bg-glow {
  position: absolute;
  pointer-events: none;
  right: -100px;
  top: -100px;
  width: 500px;
  height: 500px;
  background: radial-gradient(circle, rgba(0, 231, 1, 0.05) 0%, transparent 65%);
}

/* Dice result display */
.dice-result-display {
  width: 140px;
  height: 140px;
  border-radius: 12px;
  background: rgba(13, 27, 42, 0.7);
  border: 2px solid rgba(0, 231, 1, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  transition: border-color 0.3s ease, background 0.3s ease, box-shadow 0.3s ease;
  z-index: 1;
}

.dice-result-display.rolling {
  animation: spin 0.6s linear infinite;
  border-color: rgba(0, 231, 1, 0.5);
  box-shadow: 0 0 20px rgba(0, 231, 1, 0.3);
}

.dice-result-display.win {
  border-color: var(--accent-primary, #00e701);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  background: rgba(0, 231, 1, 0.08);
}

.dice-result-display.loss {
  border-color: var(--color-danger, #ed4245);
  box-shadow: 0 0 20px rgba(237, 66, 69, 0.4);
  background: rgba(237, 66, 69, 0.08);
}

@keyframes spin {
  from { transform: rotate(0); }
  to { transform: rotate(360deg); }
}

.dice-result-number {
  font-family: 'JetBrains Mono', monospace;
  font-size: 56px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  letter-spacing: -1px;
  transition: color 0.3s ease;
}

.dice-result-number.win { color: var(--accent-primary, #00e701); }
.dice-result-number.loss { color: var(--color-danger, #ed4245); }

/* Controls below the display */
.dice-controls {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  max-width: 360px;
  z-index: 1;
}

.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}

/* Slider */
.dice-slider-wrap {
  display: flex;
  gap: 8px;
  align-items: center;
}

.dice-slider-track {
  flex: 1;
  height: 48px;
  border-radius: 8px;
  border: 1px solid rgba(0, 231, 1, 0.15);
  position: relative;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s ease;
}

.dice-slider-handle {
  position: absolute;
  width: 32px;
  height: 32px;
  top: 50%;
  transform: translate(-50%, -50%);
  background: var(--accent-primary, #00e701);
  border-radius: 50%;
  cursor: grab;
  box-shadow: 0 4px 12px rgba(0, 231, 1, 0.4);
  border: 2px solid var(--bg-game, #0d1b2a);
  z-index: 10;
  transition: transform 0.1s ease, box-shadow 0.1s ease;
}

.dice-slider-handle:hover {
  box-shadow: 0 6px 16px rgba(0, 231, 1, 0.6);
}

.dice-slider-handle:active {
  cursor: grabbing;
}

.dice-slider-input {
  width: 70px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 231, 1, 0.2);
  border-radius: 8px;
  padding: 8px 10px;
  color: var(--accent-primary, #00e701);
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 600;
  text-align: center;
}

.dice-slider-input:focus {
  outline: none;
  border-color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.08);
  box-shadow: 0 0 12px rgba(0, 231, 1, 0.2);
}

/* Direction toggle */
.dice-dir-toggle {
  display: flex;
  gap: 6px;
  background: rgba(255, 255, 255, 0.03);
  padding: 6px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.dice-dir-btn {
  flex: 1;
  padding: 10px 12px;
  background: transparent;
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-weight: 600;
  cursor: pointer;
  border-radius: 4px;
  transition: all 150ms ease;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: inherit;
}

.dice-dir-btn:hover {
  color: var(--text-primary, #fff);
}

.dice-dir-btn.active {
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
}

/* History strip */
.history-strip {
  display: flex;
  flex-direction: row-reverse;
  gap: 4px;
  width: 100%;
  max-width: 360px;
  overflow-x: auto;
  z-index: 1;
  padding: 2px 0;
  scrollbar-width: none;
}

.history-strip::-webkit-scrollbar {
  display: none;
}

.history-pill {
  flex-shrink: 0;
  padding: 4px 8px;
  border-radius: 6px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  transition: transform 150ms ease;
  cursor: default;
}

.history-pill:hover {
  transform: translateY(-1px);
}

.history-win {
  background: rgba(0, 231, 1, 0.12);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.25);
}

.history-loss {
  background: rgba(237, 66, 69, 0.1);
  color: var(--color-danger, #ed4245);
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* Game sidebar */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}

.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}

.game-tab:hover:not(.active) {
  color: var(--text-secondary, #b1bad3);
}

.game-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
}

.game-tab-content::-webkit-scrollbar {
  width: 3px;
}

/* Bet input */
.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}

.bet-input {
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 10px 10px 24px;
  color: var(--text-primary, #fff);
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: border-color 150ms ease;
}

.bet-input:focus {
  outline: none;
  border-color: rgba(20, 117, 225, 0.5);
}

.bet-input:disabled {
  opacity: 0.5;
}

/* Bet shortcuts */
.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;
  text-align: center;
  font-family: inherit;
}

.bet-short:hover:not(:disabled) {
  background: var(--bg-hover, #2a4455);
  color: var(--color-on-dark, #fff);
}

.bet-short:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* Stats grid */
.dice-stats-grid {
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 8px;
  padding: 10px;
  background: rgba(255, 255, 255, 0.02);
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 12px;
}

.dice-stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
  text-align: center;
}

.dice-stat-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--text-tertiary, #7f8fa4);
}

.dice-stat-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
}

.dice-stat-value.gold { color: var(--accent-gold, #ffd700); }
.dice-stat-value.success { color: var(--color-success, #00e701); }

/* Action button */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}

.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}

.btn-action:disabled {
  opacity: 0.4;
  cursor: not-allowed;
  box-shadow: none;
  transform: none;
}

.btn-action:active:not(:disabled) {
  transform: translateY(0);
}

.btn-action.btn-stop {
  background: var(--color-danger, #ed4245);
  color: var(--color-on-dark, #fff);
}

.btn-action.btn-stop:hover:not(:disabled) {
  background: var(--color-danger, #c63336);
  box-shadow: 0 0 24px rgba(237, 66, 69, 0.35);
}

/* Balance row */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
}

.bal-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.bal-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}

.bal-value.green {
  color: var(--color-success, #00e701);
}

/* Auto-bet controls */
.auto-stop-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: 4px;
}

.auto-stop-item {
  display: flex;
  flex-direction: column;
}

.auto-action-row {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 4px;
}

.auto-action-toggles {
  display: flex;
  gap: 4px;
}

.auto-action-btn {
  flex: 1;
  padding: 7px 6px;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.06);
  transition: all 150ms ease;
  font-family: inherit;
}

.auto-action-btn.active {
  background: rgba(0, 231, 1, 0.12);
  color: var(--accent-primary, #00e701);
  border-color: rgba(0, 231, 1, 0.3);
}

.auto-action-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.auto-pct-wrap {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pct-label {
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}

/* Auto-bet progress */
.auto-progress {
  margin-top: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.auto-progress-bar-wrap {
  height: 6px;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 3px;
  overflow: hidden;
}

.auto-progress-bar {
  height: 100%;
  background: var(--accent-primary, #00e701);
  border-radius: 3px;
  transition: width 0.15s ease;
}

.auto-progress-info {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.profit-pos {
  color: var(--accent-primary, #00e701);
  font-weight: 700;
}

.profit-neg {
  color: var(--color-danger, #ed4245);
  font-weight: 700;
}

/* Responsive */
@media (max-width: 900px) {
  .game-sidebar {
    width: 240px;
  }
}

@media (max-width: 768px) {
  .game-body {
    flex-direction: column;
  }

  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 320px;
  }

  .game-canvas-area {
    padding: 20px;
  }

  .dice-controls {
    max-width: 100%;
  }

  .history-strip {
    max-width: 100%;
  }
}
</style>
